import React, { createContext, useContext, useState, useEffect } from 'react';

// Default preferences configuration
const DEFAULT_PREFERENCES = {
  viewModes: {
    docs: 'grid', // 'grid' or 'table'
    attendance: 'table',
    assignments: 'grid',
    // Add more sections as needed
  },
  theme: 'light', // 'light' or 'dark'
  scrumColumnVisibility: {}, // Stores visibility like { "boardId_columnId": boolean }
  scrumLayoutMode: 'grid', // 'flex' for fixed columns, 'grid' for responsive
  activeScrumTabs: {},
};

// Create context
const UIPreferencesContext = createContext();

export const UIPreferencesProvider = ({ children }) => {
  // Initialize state from localStorage or defaults
  const [preferences, setPreferences] = useState(() => {
    const savedPreferences = localStorage.getItem('uiPreferences');
    if (savedPreferences) {
      try {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(savedPreferences) };
      } catch (error) {
        console.error('Failed to parse saved preferences:', error);
        return DEFAULT_PREFERENCES;
      }
    }
    return DEFAULT_PREFERENCES;
  });

  // Update localStorage whenever preferences change
  useEffect(() => {
    localStorage.setItem('uiPreferences', JSON.stringify(preferences));
  }, [preferences]);

  // Set a specific view mode for a section
  const setViewMode = (section, mode) => {
    setPreferences(prevPrefs => ({
      ...prevPrefs,
      viewModes: {
        ...prevPrefs.viewModes,
        [section]: mode
      }
    }));
  };

  // Set theme preference
  const setTheme = (theme) => {
    setPreferences(prevPrefs => ({
      ...prevPrefs,
      theme
    }));
  };

  const setActiveScrumTab = (boardId, tabIndex) => {
    setPreferences(prevPrefs => ({
      ...prevPrefs,
      activeScrumTabs: {
        ...prevPrefs.activeScrumTabs,
        [boardId]: tabIndex,
      },
    }));
  };

  // Reset all preferences to defaults
  const resetPreferences = () => {
    setPreferences(DEFAULT_PREFERENCES);
  };

  // Custom setter for any preference
  const setPreference = (key, value) => {
    setPreferences(prevPrefs => ({
      ...prevPrefs,
      [key]: value
    }));
  };

  const toggleScrumColumnVisibility = (boardId, columnId) => {
    const key = `${boardId}_${columnId}`;
    setPreferences(prevPrefs => {
      const newScrumColumnVisibility = { ...prevPrefs.scrumColumnVisibility };
      newScrumColumnVisibility[key] = !(newScrumColumnVisibility[key] ?? true); // Default to true (visible)
      return {
        ...prevPrefs,
        scrumColumnVisibility: newScrumColumnVisibility,
      };
    });
  };

  const setScrumLayoutMode = (mode) => {
    setPreferences(prevPrefs => ({
      ...prevPrefs,
      scrumLayoutMode: mode
    }));
  };

  const value = {
    preferences,
    setViewMode,
    setActiveScrumTab,
    setTheme,
    resetPreferences,
    setPreference,
    toggleScrumColumnVisibility,
    setScrumLayoutMode,
  };

  return (
    <UIPreferencesContext.Provider value={value}>
      {children}
    </UIPreferencesContext.Provider>
  );
};

// Custom hook for using the UI preferences context
export const useUIPreferences = () => {
  const context = useContext(UIPreferencesContext);
  if (!context) {
    throw new Error('useUIPreferences must be used within a UIPreferencesProvider');
  }
  return context;
};
